import { useId } from 'react';
import { Box, Button, Collapse } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AddServiceToRealmForm } from 'RealmModule/components/SubscriptionRealm/AddServiceToRealmForm';
import { DRAFT_CAPABLE_SERVICE_NAMES } from 'IntegrationModule/api/queries/useFilterIntegrationsByTenantQuery';
import { integrationsHash } from '@constants/integration';

// Only offer services the Create Draft dropdown can actually list afterwards —
// anything else would add fine but never appear there, dead-ending the flow.
const draftCapableServiceItems = DRAFT_CAPABLE_SERVICE_NAMES.map((name) => ({
  value: name,
  name: integrationsHash[name]?.label ?? name,
})).sort((a, b) => a.name.localeCompare(b.name));

/**
 * Foldable escape hatch under the service dropdown for when the wanted
 * service isn't offered: expands into the reused Add Service To Realm form,
 * pinned to the modal's realm.
 *
 * Must stay outside the create-draft <form> element — the embedded form must
 * not nest inside it (nested forms are invalid HTML and one click would fire
 * both submit handlers).
 */
export const AddServiceToRealmSection = ({
  realmId,
  isExpanded,
  onToggle,
  onServiceAdded,
}: {
  realmId: string;
  isExpanded: boolean;
  onToggle: VoidFunction;
  /** Called after a service was successfully added to the realm. */
  onServiceAdded: VoidFunction;
}) => {
  // Per-instance so stacked modals don't render duplicate DOM ids.
  const collapseId = useId();

  return (
    <Box mt={2}>
      <Button
        size="small"
        color="inherit"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={collapseId}
        endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ textTransform: 'none', color: 'text.secondary', px: 0 }}
      >
        Can&apos;t find the integration to create?
      </Button>
      {/* mountOnEnter defers the form's sandbox/display-name fetches until the
          section is actually opened; it stays mounted afterwards so entered
          values survive re-collapse. */}
      <Collapse id={collapseId} in={isExpanded} mountOnEnter>
        <Box mt={2}>
          <AddServiceToRealmForm
            disableSelectTenantAndRealm
            realmId={realmId}
            onSuccess={onServiceAdded}
            serviceItemsOverride={draftCapableServiceItems}
          />
        </Box>
      </Collapse>
    </Box>
  );
};
